import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useCart } from "@/hooks/platform/useCart";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

type State = "checking" | "paid" | "failed";

const PaymentReturn = () => {
  const [params] = useSearchParams();
  const reference = params.get("reference") ?? params.get("trxref");
  const kind = params.get("kind");
  const { clear } = useCart();
  const navigate = useNavigate();
  const [state, setState] = useState<State>("checking");
  const [message, setMessage] = useState("Confirming your payment…");
  const ran = useRef(false);
  const [upgradeRole, setUpgradeRole] = useState<string | null>(null);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    (async () => {
      if (!reference) { setState("failed"); setMessage("No payment reference was returned."); return; }
      if (kind === "upgrade") {
        const { data, error } = await supabase.functions.invoke("role-upgrade", { body: { action: "verify", reference } });
        if (error || data?.error || !data?.paid) {
          setState("failed");
          setMessage(data?.error ?? "The upgrade payment was not completed.");
          return;
        }
        setState("paid");
        setUpgradeRole(data.target_role as string);
        setMessage(
          data.requires_verification
            ? "Payment received. Complete your company verification to finish setting up your venue."
            : "Payment received — your new tools are unlocked.",
        );
        return;
      }

      const { data, error } = await supabase.functions.invoke("paystack-verify", { body: { reference } });
      if (error || data?.error) {
        setState("failed");
        setMessage(data?.error ?? "We couldn't confirm this payment. If you were charged, it will settle shortly.");
        return;
      }
      if (data?.paid) {
        clear();
        setState("paid");
        setMessage("Payment received — your order is confirmed.");
      } else {
        setState("failed");
        setMessage("The payment was not completed.");
      }
    })();
  }, [reference, clear, kind]);

  const ticketsPath = localStorage.getItem("mcg_tickets_path") ?? "/dashboard/groover/tickets";

  return (
    <main className="min-h-screen bg-black text-white flex items-center justify-center p-6">
      <Card className="bg-gray-900/60 border-gray-800 w-full max-w-md">
        <CardContent className="p-8 text-center space-y-4">
          {state === "checking" && <Loader2 className="w-10 h-10 mx-auto animate-spin text-gray-400" />}
          {state === "paid" && <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400" />}
          {state === "failed" && <XCircle className="w-10 h-10 mx-auto text-red-400" />}
          <h1 className="text-xl font-semibold">
            {state === "paid" ? (upgradeRole ? "Upgrade complete" : "Order confirmed") : state === "failed" ? "Payment not completed" : "Please wait"}
          </h1>
          <p className="text-sm text-gray-400">{message}</p>
          {state === "paid" && upgradeRole && (
            <div className="flex gap-3 justify-center pt-2">
              <Button onClick={() => navigate(upgradeRole === "venue" ? "/dashboard/venue/verification" : "/dashboard/creator/profile")}>
                {upgradeRole === "venue" ? "Verify company" : "Open creator dashboard"}
              </Button>
            </div>
          )}
          {state === "paid" && !upgradeRole && (
            <div className="flex gap-3 justify-center pt-2">
              <Button onClick={() => navigate(ticketsPath)}>View tickets</Button>
              <Button variant="outline" asChild><Link to="/dashboard">Dashboard</Link></Button>
            </div>
          )}
          {state === "failed" && (
            <Button variant="outline" asChild className="mt-2"><Link to="/dashboard">Back to dashboard</Link></Button>
          )}
        </CardContent>
      </Card>
    </main>
  );
};

export default PaymentReturn;
